import { useEffect, useRef, useState } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { TransformControls } from '@react-three/drei'
import { useDecalStore } from '../stores/useDecalStore'
import type { DecalData } from '../types/decal'

/**
 * DecalPlacer - Interactive decal placement component
 *
 * Features:
 * - Raycasting to detect mesh surfaces
 * - Preview decal following cursor
 * - Click to place decals on meshes
 * - Transform controls for editing placed decals
 */
export default function DecalPlacer() {
  const { camera, scene, gl, raycaster } = useThree()
  const {
    isPlacementMode,
    selectedDecalUrl,
    selectedDecalId,
    placementScale,
    addDecal,
    updateDecal,
    getDecalById,
  } = useDecalStore()

  // Preview state
  const [previewData, setPreviewData] = useState<{
    position: THREE.Vector3
    normal: THREE.Vector3
    meshName: string
    meshUuid: string
    rotation: THREE.Euler
  } | null>(null)

  // Transform control mode
  const [transformMode, setTransformMode] = useState<'translate' | 'rotate'>('rotate')

  // Preview mesh ref (to exclude from raycasting)
  const previewMeshRef = useRef<THREE.Mesh>(null)

  // Preview texture aspect ratio
  const [previewTextureAspect, setPreviewTextureAspect] = useState<number>(1)

  // Loaded preview texture
  const [previewTexture, setPreviewTexture] = useState<THREE.Texture | null>(null)

  // Mouse position
  const mouseRef = useRef(new THREE.Vector2())

  // Load texture for preview (with proper disposal on URL change and unmount)
  useEffect(() => {
    if (!selectedDecalUrl) {
      setPreviewTexture(null)
      return
    }

    let disposed = false
    const loader = new THREE.TextureLoader()
    loader.load(selectedDecalUrl, (texture) => {
      if (disposed) {
        texture.dispose()
        return
      }
      if (texture.image) {
        const aspect = texture.image.width / texture.image.height
        setPreviewTextureAspect(aspect)
      }
      setPreviewTexture(texture)
    })

    return () => {
      disposed = true
      setPreviewTexture((prev) => {
        if (prev) prev.dispose()
        return null
      })
    }
  }, [selectedDecalUrl])

  // Handle mouse move for preview
  useEffect(() => {
    if (!isPlacementMode || !selectedDecalUrl) {
      setPreviewData(null)
      return
    }

    const handleMouseMove = (event: MouseEvent) => {
      // Calculate normalized device coordinates
      const rect = gl.domElement.getBoundingClientRect()
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

      // Raycast to find intersection
      raycaster.setFromCamera(mouseRef.current, camera)
      const intersects = raycaster.intersectObjects(scene.children, true)

      // Find first mesh intersection (skip preview mesh)
      for (const intersect of intersects) {
        if (intersect.object instanceof THREE.Mesh && intersect.face) {
          // Skip the preview mesh itself
          if (previewMeshRef.current && intersect.object === previewMeshRef.current) {
            continue
          }

          const mesh = intersect.object
          const normal = intersect.face.normal.clone()

          // Transform normal to world space
          const normalMatrix = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld)
          normal.applyMatrix3(normalMatrix).normalize()

          // Calculate rotation from normal
          const rotation = new THREE.Euler()
          const quaternion = new THREE.Quaternion()
          quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal)
          rotation.setFromQuaternion(quaternion)

          setPreviewData({
            position: intersect.point,
            normal,
            meshName: mesh.name || 'unnamed',
            meshUuid: mesh.uuid,
            rotation,
          })
          return
        }
      }

      // No intersection found
      setPreviewData(null)
    }

    gl.domElement.addEventListener('mousemove', handleMouseMove)

    return () => {
      gl.domElement.removeEventListener('mousemove', handleMouseMove)
    }
  }, [isPlacementMode, selectedDecalUrl, camera, scene, gl, raycaster])

  // Handle click to place decal
  useEffect(() => {
    if (!isPlacementMode || !selectedDecalUrl) return

    const handleClick = (event: MouseEvent) => {
      // Only handle left click
      if (event.button !== 0) return

      // Don't place if clicking on UI elements
      if ((event.target as HTMLElement).closest('.decal-editor, .dev-mode-panel')) {
        return
      }

      if (previewData) {
        // Get mesh from scene by raycasting again to get the actual mesh reference
        const rect = gl.domElement.getBoundingClientRect()
        mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
        mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

        raycaster.setFromCamera(mouseRef.current, camera)
        const intersects = raycaster.intersectObjects(scene.children, true)

        let targetMesh: THREE.Mesh | null = null
        for (const intersect of intersects) {
          if (intersect.object instanceof THREE.Mesh) {
            // Skip the preview mesh itself
            if (previewMeshRef.current && intersect.object === previewMeshRef.current) {
              continue
            }

            targetMesh = intersect.object
            break
          }
        }

        // Place decal at preview position
        // Note: targetMeshUuid and targetMeshName are saved for reference only
        // The spatial approach uses position + normal to find the mesh on reload
        const decalData: Omit<DecalData, 'id'> = {
          textureUrl: selectedDecalUrl,
          targetMeshName: targetMesh?.name || previewData.meshName,
          targetMeshUuid: targetMesh?.uuid || previewData.meshUuid, // Just for reference, not used for lookup
          position: {
            x: previewData.position.x,
            y: previewData.position.y,
            z: previewData.position.z,
          },
          rotation: {
            x: previewData.rotation.x,
            y: previewData.rotation.y,
            z: previewData.rotation.z,
          },
          scale: placementScale, // Use scale from UI
          aspectRatio: previewTextureAspect,
          opacity: 1,
          blendMode: 'normal',
          normal: {
            x: previewData.normal.x,
            y: previewData.normal.y,
            z: previewData.normal.z,
          },
        }

        const id = addDecal(decalData)
        console.log(
          `[DecalPlacer] Placed decal ${id} on ${targetMesh?.name || previewData.meshName}`,
          {
            position: decalData.position,
            normal: decalData.normal,
            meshName: decalData.targetMeshName,
          }
        )
      }
    }

    gl.domElement.addEventListener('click', handleClick)

    return () => {
      gl.domElement.removeEventListener('click', handleClick)
    }
  }, [isPlacementMode, selectedDecalUrl, previewData, addDecal, gl])

  // Transform controls for selected decal
  const selectedDecal = selectedDecalId ? getDecalById(selectedDecalId) : null
  const transformRef = useRef<any>(null)

  // Keyboard shortcuts for transform mode switching
  useEffect(() => {
    if (!selectedDecal || isPlacementMode) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 't') {
        setTransformMode('translate')
      } else if (event.key.toLowerCase() === 'r') {
        setTransformMode('rotate')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedDecal, isPlacementMode])

  // Handle transform changes
  const handleTransformChange = () => {
    if (!transformRef.current || !selectedDecal) return

    const controls = transformRef.current
    const position = controls.object.position
    const rotation = controls.object.rotation
    const scale = controls.object.scale

    updateDecal(selectedDecal.id, {
      position: { x: position.x, y: position.y, z: position.z },
      rotation: { x: rotation.x, y: rotation.y, z: rotation.z },
      scale: { x: scale.x, y: scale.y, z: scale.z },
    })
  }

  return (
    <>
      {/* Preview decal */}
      {isPlacementMode && selectedDecalUrl && previewData && previewTexture && (
        <mesh
          ref={previewMeshRef}
          position={previewData.position}
          rotation={previewData.rotation}
          scale={[placementScale.x * previewTextureAspect, placementScale.x, placementScale.x]}
        >
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial
            transparent
            opacity={0.5}
            side={THREE.DoubleSide}
            map={previewTexture}
          />
        </mesh>
      )}

      {/* Transform controls for selected decal */}
      {selectedDecal && !isPlacementMode && (
        <>
          <TransformControls
            ref={transformRef}
            position={[
              selectedDecal.position.x,
              selectedDecal.position.y,
              selectedDecal.position.z,
            ]}
            rotation={[
              selectedDecal.rotation.x,
              selectedDecal.rotation.y,
              selectedDecal.rotation.z,
            ]}
            scale={[selectedDecal.scale.x, selectedDecal.scale.y, selectedDecal.scale.z]}
            onChange={handleTransformChange}
            mode={transformMode}
          />
          {/* Mode indicator */}
          <div
            style={{
              position: 'absolute',
              bottom: '20px',
              left: '20px',
              padding: '8px 16px',
              backgroundColor: 'rgba(20, 20, 30, 0.9)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '4px',
              color: '#ffffff',
              fontFamily: 'monospace',
              fontSize: '12px',
              zIndex: 1000,
            }}
          >
            Transform: <strong>{transformMode === 'translate' ? 'Move (T)' : 'Rotate (R)'}</strong>
          </div>
        </>
      )}
    </>
  )
}
